<script setup lang="ts">
type Emit = {
  (event: 'update:modelValue', value: string): void
}

interface Props {
  modelValue?: string
}

const emit = defineEmits<Emit>()

const props = withDefaults(defineProps<Props>(), {
  modelValue: ''
})

const unformattedValue = ref(props.modelValue || '')

const formatPolicyNumber = (policyNumber: string): string => {
  const cleaned = policyNumber.replace(/\D/g, '').substring(0, 8)
  return cleaned.length > 4 ? `${cleaned.substring(0, 4)}-${cleaned.substring(4)}` : cleaned
}

const formatEmittedValue = (policyNumber: string): string => {
  const cleaned = policyNumber.replace(/\D/g, '').substring(0, 8)
  return cleaned.length > 0
    ? `EP${cleaned.length > 4 ? `${cleaned.substring(0, 4)}-${cleaned.substring(4)}` : cleaned}`
    : ''
}

const updateValue = (rawValue: string) => {
  const numericValue = rawValue.replace(/\D/g, '').substring(0, 8)
  unformattedValue.value = numericValue

  const formattedValue = numericValue.length > 0 ? formatEmittedValue(numericValue) : ''
  emit('update:modelValue', formattedValue)
}

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const currentPos = target.selectionStart || 0

  const beforeFormat = target.value

  updateValue(target.value)

  const formattedValue = formatPolicyNumber(unformattedValue.value)
  if (formattedValue !== beforeFormat) {
    target.value = formattedValue

    // Adjust the cursor position only if needed
    const diff = formattedValue.length - beforeFormat.length
    const newPos = currentPos + (diff > 0 ? 1 : 0)
    target.setSelectionRange(newPos, newPos)
  }
}

const handlePaste = (event: ClipboardEvent) => {
  const pastedData = event.clipboardData?.getData('text') || ''
  updateValue(pastedData)
  event.preventDefault()
}

const value = computed({
  get() {
    return formatPolicyNumber(unformattedValue.value)
  },
  set(val) {
    updateValue(val)
  }
})

watch(
  () => props.modelValue,
  (val) => {
    unformattedValue.value = val || ''
  }
)
</script>

<template>
  <div class="relative">
    <span class="absolute inset-y-0 left-0 flex items-center pl-3">EP</span>
    <Input
      id="policy-number"
      v-model="value"
      placeholder="0000-0000"
      class="policy-number"
      @input="handleInput"
      @paste="handlePaste"
    />
  </div>
</template>

<style scoped>
.policy-number {
  :deep(input) {
    @apply pl-[30px];
  }
}
</style>
